import { PRIVILEGES } from '@/constants/privileges'
import { canPlaceOrder, PLACE_ORDER_KEY } from './placeOrderButton.utils'

/**
 * With `getUserByCrmIdV3` ON the project detail buttons follow the v3 privileges instead of masters
 * `/v1/configurations`: that endpoint matches on the session's first role, which the new privilege
 * generation no longer keeps as the operational one (roles come sorted by id, so most users resolve
 * to webUser), and users got the wrong set of buttons. A button without its privilege is not listed.
 *
 * Order, importance and icons are the ones masters stores for ProjectDetails, merged across roles.
 * Menu-only options (share, fork, discard) are not listed here: ProjectActionsMenu injects them.
 */
const has = (privilege) => (account) => !!account?.privileges?.includes(privilege)

const ORDINARY = [
	{ button: { key: 'button.project.generateBudget', importance: 0 }, isAllowed: has(PRIVILEGES.BUDGET_ADD) },
	{ button: { key: 'button.project.requestBudget', importance: 0 }, isAllowed: has(PRIVILEGES.BUDGET_REQUEST) },
	{ button: { key: 'button.project.registrationQuotes', importance: 0 }, isAllowed: has(PRIVILEGES.PROJECT_REGISTRATION) },
	{ button: { key: 'button.project.requestSupport', importance: 0, icon: 'support' }, isAllowed: has(PRIVILEGES.SUPPORT_REQUEST) },
	{ button: { key: PLACE_ORDER_KEY, importance: 0 }, isAllowed: canPlaceOrder },
	{ button: { key: 'button.project.add', importance: 1 }, isAllowed: has(PRIVILEGES.PROJECT_ADD) }
]

const CONTEXTUAL = [
	{ button: { key: 'button.project.assignThirdParty', importance: 1, icon: 'send' }, isAllowed: has(PRIVILEGES.PROJECT_ASSIGN_THIRD_PARTY) },
	{ button: { key: PLACE_ORDER_KEY, importance: 1, icon: 'assign' }, isAllowed: canPlaceOrder },
	{ button: { key: 'button.project.requestSupport', importance: 1, icon: 'support' }, isAllowed: has(PRIVILEGES.SUPPORT_REQUEST) },
	{ button: { key: 'button.project.download', importance: 1, icon: 'download' }, isAllowed: has(PRIVILEGES.PROJECT_DOWNLOADS) },
	{ button: { key: 'button.project.version', importance: 0, icon: 'document' }, isAllowed: has(PRIVILEGES.PROJECT_VERSION_ADD) },
	{ button: { key: 'button.project.duplicate', importance: 0, icon: 'duplicate' }, isAllowed: has(PRIVILEGES.PROJECT_CLONE) },
	{ button: { key: 'button.project.delete', importance: 0, icon: 'delete' }, isAllowed: has(PRIVILEGES.PROJECT_DELETE) }
]

const allowedButtons = (definitions, account) =>
	definitions.filter(({ isAllowed }) => isAllowed(account)).map(({ button }) => ({ ...button }))

export const buildProjectDetailsButtons = (account) => ({
	ORDINARY: allowedButtons(ORDINARY, account),
	CONTEXTUAL: allowedButtons(CONTEXTUAL, account)
})
